// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// types
import type { PurchaseHistoryCompletionServiceData, StateTree } from '@makemydeal/dr-dash-types';
import { PURCHASE_HISTORY_MAX_RECORDS } from '@makemydeal/dr-dash-types';

// selectors
import { getDealXgId } from '../selectors/offerRedux';
import { getOfferGlobalCustomerId } from '../selectors/offerSelectors';

// consts/enums
import {
    PURCHASE_HISTORY_FETCH_FAILURE,
    PURCHASE_HISTORY_FETCH_REQUEST,
    PURCHASE_HISTORY_FETCH_SUCCESS
} from '../actionTypes/purchaseHistoryActionTypes';
import {
    PURCHASE_HISTORY_EVENT_RECEIVED,
    PURCHASE_HISTORY_NOTIFICATION_ACTIVATED,
    PURCHASE_HISTORY_NOTIFICATION_DISMISSED,
    PURCHASE_HISTORY_NOTIFICATION_RAISED,
    PURCHASE_HISTORY_REQUESTED,
    PURCHASE_HISTORY_REQUEST_ACCEPTED,
    PURCHASE_HISTORY_REQUEST_FAILED,
    PURCHASE_HISTORY_REQUEST_UNAVAILABLE,
    PURCHASE_HISTORY_TAKING_LONGER,
    PURCHASE_HISTORY_TIMED_OUT
} from '../actionTypes/purchaseHistoryActionTypes';

export type PurchaseHistoryRequestedAction = {
    type: typeof PURCHASE_HISTORY_REQUESTED;
    payload: { requestedAtMs: number };
};

export type PurchaseHistoryRequestAcceptedAction = {
    type: typeof PURCHASE_HISTORY_REQUEST_ACCEPTED;
    payload: { correlationId: string | null };
};

export type PurchaseHistoryRequestUnavailableAction = {
    type: typeof PURCHASE_HISTORY_REQUEST_UNAVAILABLE;
};

export type PurchaseHistoryRequestFailedAction = {
    type: typeof PURCHASE_HISTORY_REQUEST_FAILED;
};

export type PurchaseHistoryEventReceivedAction = {
    type: typeof PURCHASE_HISTORY_EVENT_RECEIVED;
    payload: PurchaseHistoryCompletionServiceData;
};

export type PurchaseHistoryTakingLongerAction = {
    type: typeof PURCHASE_HISTORY_TAKING_LONGER;
};

export type PurchaseHistoryTimedOutAction = {
    type: typeof PURCHASE_HISTORY_TIMED_OUT;
};

export type PurchaseHistoryNotificationRaisedAction = {
    type: typeof PURCHASE_HISTORY_NOTIFICATION_RAISED;
};

export type PurchaseHistoryNotificationActivatedAction = {
    type: typeof PURCHASE_HISTORY_NOTIFICATION_ACTIVATED;
    payload: { uniqueScrollRequestId: string | boolean };
};

export type PurchaseHistoryNotificationDismissedAction = {
    type: typeof PURCHASE_HISTORY_NOTIFICATION_DISMISSED;
};

/** Union for reducer type-checking. */
export type PurchaseHistoryActions =
    | PurchaseHistoryRequestedAction
    | PurchaseHistoryRequestAcceptedAction
    | PurchaseHistoryRequestUnavailableAction
    | PurchaseHistoryRequestFailedAction
    | PurchaseHistoryEventReceivedAction
    | PurchaseHistoryTakingLongerAction
    | PurchaseHistoryTimedOutAction
    | PurchaseHistoryNotificationRaisedAction
    | PurchaseHistoryNotificationActivatedAction
    | PurchaseHistoryNotificationDismissedAction;

/**
 * Asks the proxy endpoint for this deal's purchase history.
 *
 * Every identifier travels in the body, matching the sibling `/v2/insights` request shape:
 * `globalCustomerId`, `dealId` and `dealerId`, all used as supplied. The endpoint resolves nothing and
 * makes a single downstream call.
 *
 * There is no `?dealXgId=` query parameter and no `x-dr-dealxgid` header, because the route is
 * protected by `jwtSessionCreateAuthorizer` (token only) rather than `dealCentralAuthorizer` — so
 * nothing needs the deal outside the body. What that gives up is recorded in plan.md's deviation
 * table.
 */
export const requestPurchaseHistory = (state: StateTree, maxRecords: number = PURCHASE_HISTORY_MAX_RECORDS): RSAACallAction => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '');
    const dealXgId = getDealXgId(state);
    const globalCustomerId = getOfferGlobalCustomerId(state);
    const dealerId = dealerSelectors.getDealerId(state);

    const request: RSAACallRequest = {
        endpoint: `${bffApiUrlResult.url}/v3/deal-pulse/purchase-history`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            globalCustomerId,
            dealId: dealXgId,
            dealerId: dealerId ? String(dealerId) : undefined,
            maxRecords
        }),
        types: [PURCHASE_HISTORY_FETCH_REQUEST, PURCHASE_HISTORY_FETCH_SUCCESS, PURCHASE_HISTORY_FETCH_FAILURE]
    };

    return { [RSAA]: request };
};

export const purchaseHistoryRequested = (requestedAtMs: number): PurchaseHistoryRequestedAction => ({
    type: PURCHASE_HISTORY_REQUESTED,
    payload: { requestedAtMs }
});

export const purchaseHistoryRequestAccepted = (correlationId: string | null): PurchaseHistoryRequestAcceptedAction => ({
    type: PURCHASE_HISTORY_REQUEST_ACCEPTED,
    payload: { correlationId }
});

export const purchaseHistoryRequestUnavailable = (): PurchaseHistoryRequestUnavailableAction => ({
    type: PURCHASE_HISTORY_REQUEST_UNAVAILABLE
});

export const purchaseHistoryRequestFailed = (): PurchaseHistoryRequestFailedAction => ({
    type: PURCHASE_HISTORY_REQUEST_FAILED
});

export const purchaseHistoryEventReceived = (
    completion: PurchaseHistoryCompletionServiceData
): PurchaseHistoryEventReceivedAction => ({
    type: PURCHASE_HISTORY_EVENT_RECEIVED,
    payload: completion
});

export const purchaseHistoryTakingLonger = (): PurchaseHistoryTakingLongerAction => ({
    type: PURCHASE_HISTORY_TAKING_LONGER
});

export const purchaseHistoryTimedOut = (): PurchaseHistoryTimedOutAction => ({
    type: PURCHASE_HISTORY_TIMED_OUT
});

export const purchaseHistoryNotificationRaised = (): PurchaseHistoryNotificationRaisedAction => ({
    type: PURCHASE_HISTORY_NOTIFICATION_RAISED
});

export const purchaseHistoryNotificationActivated = (
    uniqueScrollRequestId: string | boolean
): PurchaseHistoryNotificationActivatedAction => ({
    type: PURCHASE_HISTORY_NOTIFICATION_ACTIVATED,
    payload: { uniqueScrollRequestId }
});

export const purchaseHistoryNotificationDismissed = (): PurchaseHistoryNotificationDismissedAction => ({
    type: PURCHASE_HISTORY_NOTIFICATION_DISMISSED
});
